// Keyword Constants for Estimating System
export const SCOPE_ACTION_KEYWORDS = [
  'remodel', 'redo', 'renovate', 'new', 'build', 'estimate', 'quote',
  'overhaul', 'update', 'gut', 'tear', 'demo', 'replace', 'fix', 'upgrade',
  'scope', 'price', 'cost', 'bid', 'proposal', 'takeoff', 'create',
  'start', 'begin', 'do', 'make', 'full', 'complete', 'total', 'entire',
  'add', 'need', 'want', 'list', 'standard', 'typical', 'generic',
];

// --- SMART TYPE DETECTION ---
// ═══════════════════════════════════════════════════════════════════════════
// DETERMINISTIC CLASSIFICATION RULES - Hard-Coded, No Guessing
// ═══════════════════════════════════════════════════════════════════════════

// MATERIAL KEYWORDS - These ALWAYS result in MAT, regardless of other keywords
export const MATERIAL_OVERRIDE_KEYWORDS = [
  'faucet', 'sink', 'toilet', 'vanity', 'lvp', 'flooring', 'tile',
  'cabinet', 'countertop', 'backsplash', 'baseboard', 'trim', 'molding',
  'lumber', 'wood', 'plywood', 'drywall', 'underlayment', 'thinset',
  'grout', 'caulk', 'silicone', 'primer', 'supplies', 'kit',
  'fixture', 'light', 'outlet', 'switch', 'hardware', 'knob', 'handle',
  'lock', 'lever', 'hinge', 'slider', 'door', 'window',
];

// ═══════════════════════════════════════════════════════════════════════════
// FIX: SECTION NAME CLEANER - Prevents recursive "01. 01. 01..." numbering
// Strips any existing number prefixes before applying a new one
// ═══════════════════════════════════════════════════════════════════════════
function cleanSectionName(name) {
  if (!name) return 'GENERAL';
  
  // Strip ALL leading number patterns like "01. ", "01. 01. ", "1. 2. 3. " etc.
  // Pattern matches: one or more occurrences of (digits + period + optional space)
  const cleaned = name.replace(/^(\d+\.\s*)+/g, '').trim();
  
  return cleaned || 'GENERAL';
}

// Helper to format section name with index (only prepends number once)
function formatSectionName(index, rawName) {
  const cleanName = cleanSectionName(rawName);
  const prefix = String(index + 1).padStart(2, "0");
  return `${prefix}. ${cleanName.toUpperCase()}`;
}

// LABOR KEYWORDS - If ANY match (and no material override), item is LABOR
export const LABOR_KEYWORDS = [
  ' labor', 'labor ', 'labour',   // Labor with space boundaries
  'install', 'installation',       // Installation work
  'demo ', ' demo', 'demolition',  // Demolition work (avoid "demo" in product names)
  'removal', 'remove',             // Removal work
  ' hours', ' hour ', 'hourly',    // Time-based billing
  'painting', 'paint labor', 'paint work', // Painting work (not paint products)
  'apply', 'application',          // Application work
  'prep ', ' prep', 'preparation', // Prep work
  'haul', 'hauling', 'haul-off',   // Hauling
  'cleanup', 'clean-up', 'clean up', // Cleanup
  'disposal',                      // Disposal work
  'rough-in', 'rough in',          // Rough-in work
  'trim-out', 'trim out',          // Trim-out work
  'framing',                       // Framing work
  'hanging', ' hang ',             // Hanging work
  'mounting',                      // Mounting work
  'finishing labor', 'finish work', // Finishing work
  'setting ', ' set up', 'setup',  // Setting work
  'laying',                        // Laying work
  'protection', 'protect',         // Protection/prep work
];

// ═══════════════════════════════════════════════════════════════════════════
// MARKET AVERAGE PRICING - For Zero-Cost Item Backfill
// ═══════════════════════════════════════════════════════════════════════════
export const MARKET_AVERAGES = {
  // Door Hardware
  'passage set': 35.00,
  'passage': 35.00,
  'privacy lever': 45.00,
  'privacy': 45.00,
  'lever': 45.00,
  'bifold hardware': 25.00,
  'bifold': 25.00,
  'garage entry': 65.00,
  'entry lock': 65.00,
  'deadbolt': 45.00,
  'door knob': 25.00,
  'knob': 15.00,
  'handle': 20.00,
  'hinge': 8.00,
  'door stop': 5.00,
  'slider kit': 35.00,
  'slider': 35.00,
  'pocket door': 85.00,
  'barn door hardware': 120.00,
  
  // Window Hardware
  'window lock': 15.00,
  'window latch': 12.00,
  
  // Cabinet Hardware
  'cabinet pull': 8.00,
  'cabinet knob': 6.00,
  'drawer slide': 15.00,
  'soft close': 12.00,
  
  // Electrical (materials)
  'outlet': 3.00,
  'switch': 3.00,
  'cover plate': 2.00,
  'junction box': 5.00,
  'wire nut': 0.50,
  
  // Plumbing (materials)
  'supply line': 12.00,
  'p-trap': 15.00,
  'shut off valve': 18.00,
  'wax ring': 5.00,
  'flange': 8.00,
  
  // Fasteners & Misc
  'screw': 0.10,
  'nail': 0.05,
  'anchor': 0.25,
  'bracket': 5.00,
  'shim': 3.00,
};

// ═══════════════════════════════════════════════════════════════════════════
// LAZY ESTIMATOR BLOCKER - Tier 1 Quality Validation
// ═══════════════════════════════════════════════════════════════════════════

// Forbidden single-word phrases (Lazy Estimator violations)
export const FORBIDDEN_PHRASES = [
  'demo', 'plumbing', 'electrical', 'flooring', 'painting', 'framing',
  'drywall', 'hvac', 'roofing', 'concrete', 'trim', 'cabinets',
  'fixtures', 'cleanup', 'general', 'misc', 'materials', 'labor'
];

// Auto-correction mapping for lazy phrases
export const LAZY_PHRASE_CORRECTIONS = {
  'demo': 'Demolition Labor (Specify: fixture demo, flooring removal, etc.)',
  'plumbing': 'Plumbing Rough-In / Plumbing Trim-Out (Specify scope)',
  'electrical': 'Electrical Rough-In / Electrical Trim-Out (Specify scope)',
  'flooring': 'Flooring Installation Labor / Flooring Material (Specify type)',
  'painting': 'Interior Painting Labor / Paint Material (Specify rooms)',
  'framing': 'Wall Framing Labor / Framing Lumber (Specify LF or SF)',
  'drywall': 'Drywall Hang & Finish Labor / Drywall Boards (Specify SF)',
  'hvac': 'HVAC Rough-In / HVAC Equipment (Specify system)',
  'roofing': 'Roofing Labor / Roofing Material (Specify type & SF)',
  'concrete': 'Concrete Work / Concrete Material (Specify CY)',
  'trim': 'Finish Carpentry Labor / Trim Material (Specify LF)',
  'cabinets': 'Cabinet Installation Labor / Cabinets (Specify type & count)',
  'fixtures': 'Fixture Installation Labor / Fixtures (Specify items)',
  'cleanup': 'Daily Cleanup Labor / Final Cleanup & Haul-Off',
  'general': 'General Conditions / Supervision (Specify hours)',
  'misc': 'Miscellaneous Materials (Itemize specifics)',
  'materials': 'Materials (MUST itemize - no lump sums allowed)',
  'labor': 'Labor (MUST specify trade and scope)',
};
